import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { formatMessage } from "lib/globalize-util";
import { snackbarAdd } from "actions/snackbars";
import { Observable } from "rxjs";
import { mapToOperation, getAndIncrementRequestId } from "./helpers";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import { List, Map } from "immutable";
import { listInsertOperation, listSetOperation, listRemoveOperation } from "actions/mutate";

export const addReviewDisplayPreferenceEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.REVIEW_TAB.REVIEWDISPLAYPREFERENCES.ADD.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, displayPreference, index = 0 }) => [
            listInsertOperation(id, displayPreference, index), // always add at top of list for now
            snackbarAdd({ message: formatMessage("View saved!") }),
        ]);

// update a single display preference
// flows into syncItemDisplayPreferencesEpic after going through mutation epics
export const editReviewDisplayPreferenceEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.REVIEW_TAB.REVIEWDISPLAYPREFERENCES.EDIT.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, index, displayPreference }) => {
            return [
                listSetOperation(id, index, Map(displayPreference)),
                snackbarAdd({ message: formatMessage("View updated!") }),
            ];
        });

export const removeReviewDisplayPreferencesEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.REVIEW_TAB.REVIEWDISPLAYPREFERENCES.REMOVE.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, index }) => [
            listRemoveOperation(id, index),
            snackbarAdd({ message: formatMessage("View removed.") }),
        ]);

// // sync display preference items to the server (add, update, delete, move)
// // triggered after mutation epics have run
export const syncReviewDisplayPreferencesEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.REVIEW_TAB.SYNC.START)
        // Dont proceed if there is something sending or there is nothing to send
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(
            ({
                payload: { objectId, operations: iuOperations },
            }: {
                payload: { objectId: string; operations?: any[] };
            }) => {
                const { collection, mutate } = store.getState();
                const { committedHEADs, uncommittedMutations } = mutate;
                const { sessionId, merchantId } = collection;

                const committedHEAD = committedHEADs.get(objectId);

                const operations = [];
                uncommittedMutations.get(objectId).forEach((mutation) => {
                    mutation.operations.forEach((operation) => {
                        operations.push(mapToOperation(operation));
                    });
                });
                const firstOperation = (iuOperations ?? []).find((op) => !!op);
                const review_id =
                    firstOperation.operation === "remove"
                        ? mutate.snapshots.getIn(["review_display_preferences", 0, "id"])
                        : firstOperation?.value.get("id");
                const requestId = getAndIncrementRequestId(objectId);
                const revision = parseInt(committedHEAD.revision, 10) + 1;
                const command = {
                    operations,
                    revision,
                    req_id: requestId,
                };

                store.dispatch({
                    type: MUTATE_OPERATIONS_SENT,
                    payload: {
                        requestId,
                        objectId,
                        revision,
                        objectType: committedHEAD.object_type,
                    },
                });

                return {
                    command,
                    sessionId,
                    requestId,
                    collectionId: committedHEAD.document_id,
                    merchantId,
                    review_id,
                };
            }
        )
        // Make it a queue, wait for response before continuing
        .concatMap(({ command, collectionId, requestId, sessionId, merchantId, review_id }) => {
            const url = `/v1/collections/${collectionId}/review_preference/${review_id}/mutate?sid=${sessionId}&req_id=${requestId}`;
            return ajax({
                body: { ...command, merchant_id: merchantId },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId },
                    type: COLLECTIONS.COLLECTION.ITEMS.REVIEW_TAB.SYNC.COMPLETED,
                }))
                .catch((error) => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error saving the view. Please try again."),
                            })
                        )
                    );
                });
        });
